import { useCallback, useEffect, useMemo, useState, type RefObject } from 'react';

import {
  ENABLE_VIEWPORT_EDITOR_ACTIVATION,
  VIEWPORT_EDITOR_ROOT_MARGIN,
} from '@/lib/editor-viewport-activation-flags';

export type EditorActivationPinReason =
  | 'focus'
  | 'menu'
  | 'selection'
  | 'modal'
  // A block-menu action (delete / add-above / add-below) is resolving the
  // block's doc id and flushing pending edits. Distinct from `'menu'` —
  // that reason is cleared as soon as the menu closes (before this async
  // work even starts), so reusing it here would let the effect that mirrors
  // `openMenuId` immediately unpin while the action is still in flight.
  | 'pending-action';

export type EditorActivation = {
  shouldMountEditor: boolean;
  pin: (reason: EditorActivationPinReason) => void;
  unpin: (reason: EditorActivationPinReason) => void;
};

function intersectionObserverAvailable(): boolean {
  return typeof IntersectionObserver !== 'undefined';
}

/**
 * Gates EditorJS mounting to "near the viewport" via `IntersectionObserver`
 * with a generous `rootMargin` (see `editor-viewport-activation-flags.ts`).
 * This hook is deliberately dumb about EditorJS itself — it only tracks
 * whether the observed element is near the viewport and whether any
 * interaction-critical "pin" is currently held, and exposes a single
 * `shouldMountEditor` boolean. Callers (the paragraph cluster / list
 * components) own the actual mount → flush → destroy → static-render →
 * reactivate sequencing off of `shouldMountEditor` transitions; none of
 * that lifecycle logic lives here.
 *
 * `shouldMountEditor` is true when EITHER:
 *  - the observed element currently intersects the viewport (within
 *    `rootMargin`), OR
 *  - at least one pin is held — pins always win over the viewport signal,
 *    so a block mid-interaction (focused, a menu/toolbar open, an active
 *    selection, or in use as an EditLink source) never gets its editor
 *    torn out from under the user just because a fast scroll or a tall
 *    sibling pushed it outside the observed bounds.
 *
 * Falls back to permanently `true` (today's "always mounted" behavior)
 * when the feature flag is off or `IntersectionObserver` isn't available
 * (older browsers, and the default jsdom test environment — existing
 * cluster/list tests rely on this fallback to keep passing unmodified).
 */
export function useViewportEditorActivation<T extends Element>(
  elementRef: RefObject<T | null>,
): EditorActivation {
  const alwaysMounted =
    !ENABLE_VIEWPORT_EDITOR_ACTIVATION || !intersectionObserverAvailable();

  const [isIntersecting, setIsIntersecting] = useState<boolean>(alwaysMounted);
  const [pinnedReasons, setPinnedReasons] = useState<
    ReadonlySet<EditorActivationPinReason>
  >(() => new Set());

  useEffect(() => {
    if (alwaysMounted) return;
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      entries => {
        // Only one target is ever observed per hook instance — the last
        // entry is always the current (and only) one.
        const entry = entries[entries.length - 1];
        if (entry) setIsIntersecting(entry.isIntersecting);
      },
      { rootMargin: VIEWPORT_EDITOR_ROOT_MARGIN, threshold: 0 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [alwaysMounted, elementRef]);

  const pin = useCallback((reason: EditorActivationPinReason) => {
    setPinnedReasons(prev => {
      if (prev.has(reason)) return prev;
      const next = new Set(prev);
      next.add(reason);
      return next;
    });
  }, []);

  const unpin = useCallback((reason: EditorActivationPinReason) => {
    setPinnedReasons(prev => {
      if (!prev.has(reason)) return prev;
      const next = new Set(prev);
      next.delete(reason);
      return next;
    });
  }, []);

  const shouldMountEditor =
    alwaysMounted || isIntersecting || pinnedReasons.size > 0;

  return useMemo(
    () => ({ shouldMountEditor, pin, unpin }),
    [shouldMountEditor, pin, unpin],
  );
}
